import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect } from 'react';
import { useEventLogging } from '../../../models/Logging';
import { useLambda } from '../../../models/hooks/useLambda';
import { TinyPopover } from '../../../web/components/TinyPopover';
import earthGraphic from '../../assets/earth_graphic.svg';
import { useQueue } from '../../hooks/queue';
import { TransparentButton } from '../TransparentButton';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
}

const ClientsNearMeBase: React.FC<IProps> = ({ className = '' }) => {
	const queue = useQueue();
	const [isOpen, , setIsOpenLambda] = useLambda(false);
	const { logApiError } = useEventLogging();

	const loadClientsNearMe = async () => {
		const vertical = queue.lead?.company?.tags
			?.find((tag: string) => tag.includes('Vertical:'))
			?.replace('Vertical: ', '');
		queue.lead?.getClientsNearMe(vertical).catch(err => logApiError('GetNearbyClients-Error', err));
	};

	useEffect(() => {
		loadClientsNearMe();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queue.companyId]);

	const clientsNearMe = queue.lead?.clientsNearMe;
	const customers = clientsNearMe?.customers;

	const anchor = () => (
		<div className={css(styleSheet.globe)} onMouseEnter={setIsOpenLambda(true)}>
			<img src={earthGraphic} alt='small image of earth' />

			{clientsNearMe?.total > 0 && <div className={css(styleSheet.bubble)}> {clientsNearMe?.total}</div>}
		</div>
	);

	return (
		<TransparentButton className={`${css(styleSheet.clientsNearMeContainer)} ${className}`}>
			<div>Clients Near Me</div>
			<TinyPopover
				isOpen={isOpen && clientsNearMe?.total > 0}
				anchor={anchor()}
				placement={['right']}
				dismissOnOutsideAction={true}
				onRequestClose={setIsOpenLambda(false)}
			>
				<div className={css(styleSheet.popoverContainer)}>
					<div className={css(styleSheet.groupingsColumn)}>
						<div className={css(styleSheet.totalClients)}>{clientsNearMe?.total} Total Clients</div>
						<div className={css(styleSheet.groupingsList)}>
							{clientsNearMe?.groupings?.map(x => {
								return <div key={x.label}>{x.total + ' in ' + x.label}</div>;
							})}
						</div>
					</div>
					{customers?.length > 0 && (
						<div className={css(styleSheet.customersColumn)}>
							<div className={css(styleSheet.customersSectionHeader)}>Nearby Customers</div>
							<div className={css(styleSheet.customersList)}>
								{customers.map(customer => (
									<div key={customer.companyName} className={css(styleSheet.customerCard)}>
										{customer.companyName && <div className={css(styleSheet.customerName)}>{customer.companyName}</div>}
										{customer.location && <div className={css(styleSheet.customerLocation)}>{customer.location}</div>}
										<div className={css(styleSheet.customerMeta)}>
											{customer.yearsAsCustomer != null && (
												<span className={css(styleSheet.customerYears)}>
													{customer.yearsAsCustomer} {customer.yearsAsCustomer === 1 ? 'year' : 'years'}
												</span>
											)}
											{customer.industry && (
												<span className={css(styleSheet.customerIndustry)}>{customer.industry}</span>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</TinyPopover>
		</TransparentButton>
	);
};

export const ClientsNearMe = observer(ClientsNearMeBase);
